import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import './BusinessEntityForm.css';

const BUSINESS_TYPES = [
  'LLC',
  'Corporation',
  'S-Corporation',
  'Partnership',
  'Sole Proprietorship',
  'Other',
];

const BusinessEntityForm = ({ entity, onSubmit, onCancel }) => {
  const t = useTranslation();
  const isMountedRef = useRef(true);
  
  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  const [formData, setFormData] = useState({
    businessName: '',
    dateCreated: '',
    ein: '',
    businessType: '',
    industry: '',
    address: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (entity) {
      // Convert Firestore timestamp to date string if needed
      const dateCreated = entity.dateCreated?.toDate 
        ? entity.dateCreated.toDate().toISOString().split('T')[0]
        : (entity.dateCreated || '');

      setFormData({
        businessName: entity.businessName || '',
        dateCreated: dateCreated,
        ein: entity.ein || '',
        businessType: entity.businessType || '',
        industry: entity.industry || '',
        address: entity.address || '',
      });
    }
  }, [entity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = t('creditCardsInventory.businessNameRequired');
    }

    if (!formData.dateCreated) {
      newErrors.dateCreated = t('creditCardsInventory.dateCreatedRequired');
    }

    // EIN validation (optional but if provided, should be valid format)
    if (formData.ein && !/^\d{2}-?\d{7}$/.test(formData.ein.replace(/\s/g, ''))) {
      newErrors.ein = t('creditCardsInventory.einInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const submitData = {
        businessName: formData.businessName.trim(),
        dateCreated: formData.dateCreated,
        ein: formData.ein.trim() || null,
        businessType: formData.businessType || null,
        industry: formData.industry.trim() || null,
        address: formData.address.trim() || null,
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setErrors({ submit: t('creditCardsInventory.failedToSaveEntity') });
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form className="business-entity-form" onSubmit={handleSubmit}>
      <div className="business-entity-form-header">
        <h3>{entity ? t('creditCardsInventory.editBusinessEntity') : t('creditCardsInventory.addBusinessEntity')}</h3>
      </div>

      <div className="business-entity-form-body">
        {errors.submit && (
          <div className="form-error-message">{errors.submit}</div>
        )}

        <div className="form-group">
          <label htmlFor="businessName">
            {t('creditCardsInventory.businessName')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="businessName"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            placeholder={t('creditCardsInventory.enterBusinessName')}
            className={errors.businessName ? 'error' : ''}
          />
          {errors.businessName && <span className="field-error">{errors.businessName}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dateCreated">
              {t('creditCardsInventory.dateCreated')} <span className="required">*</span>
            </label>
            <input
              type="date"
              id="dateCreated"
              name="dateCreated"
              value={formData.dateCreated}
              onChange={handleChange}
              className={errors.dateCreated ? 'error' : ''}
            />
            {errors.dateCreated && <span className="field-error">{errors.dateCreated}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="businessType">{t('creditCardsInventory.businessType')}</label>
            <select
              id="businessType"
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
            >
              <option value="">{t('creditCardsInventory.selectType')}</option>
              {BUSINESS_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ein">{t('creditCardsInventory.ein')}</label>
            <input
              type="text"
              id="ein"
              name="ein"
              value={formData.ein}
              onChange={handleChange}
              placeholder={t('creditCardsInventory.einPlaceholder')}
              className={errors.ein ? 'error' : ''}
            />
            {errors.ein && <span className="field-error">{errors.ein}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="industry">{t('creditCardsInventory.industry')}</label>
            <input
              type="text"
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              placeholder={t('creditCardsInventory.industryPlaceholder')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="address">{t('creditCardsInventory.businessAddress')}</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder={t('creditCardsInventory.enterBusinessAddress')}
            rows="3"
          />
        </div>
      </div>

      <div className="business-entity-form-footer">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('creditCardsInventory.cancel')}
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('creditCardsInventory.saving') : (entity ? t('creditCardsInventory.update') : t('creditCardsInventory.add'))} {t('creditCardsInventory.entity')}
        </button>
      </div>
    </form>
  );
};

export default BusinessEntityForm;

